import { cx } from '@emotion/css';
import { Icon } from '@mui/material';
import Big from 'big.js';
import React, { useState, useCallback, useEffect } from 'react';
import { isEven } from '../../../utils/isEven';
import { ix } from '../../../utils/ix';
import { Table, TableBody, TableCell, TableHead, TablePagination, TableRow } from '../../atoms';
import { Typography } from '../../electrons';
import { ColumnSelector } from '../columnSelectors/ColumnSelector';
import { useStyles } from './PaginationTable.styles';
import { ColumnType } from './PaginationTable.types';
import { CELL_ID, COLUMN_SELECTOR_ID, DEFAULT_SELECTED_COLUMN_COUNT, HEADER_COLUMN_SELECTOR, NOT_CONSIDERED_EMPTY, TOTALABLE_COLUMN_TYPES, TOTALS_ID, } from './constants';
/**
 * @deprecated
 *
 * This component is deprecated. Please use DataGrid implementation.
 */
export function PaginationTable({ className, id, rows, columns, columnSelector = false, defaultSelectedColumnIds, ColumnSelectorProps, columnSelectorAlwaysEnabled = new Set(), columnSelectorDefaultMetrics = new Set(), columnSelectorPinnedColumns = [], noDataMessage, rowHover, rowsPerPageOptions, removeFooter, totalsRow, }) {
    const classes = useStyles();
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions ? rowsPerPageOptions[0] : 10);
    const [sortColumn, setSortColumn] = useState();
    const [sortDirection, setSortDirection] = useState();
    const [columnsSelected, setColumnsSelected] = useState(defaultSelectedColumnIds || columns.slice(0, DEFAULT_SELECTED_COLUMN_COUNT).map((column) => column.id));
    const [columnSelectorOpen, setColumnSelectorOpen] = useState(false);
    const onPageChange = useCallback((_event, newPage) => {
        setPage(newPage);
    }, []);
    const onRowsPerPageChange = useCallback((event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    }, []);
    const decrementPageIfNeeded = () => {
        const notOnFirstPage = page !== 0;
        const notEnoughRowsToShow = rows.length <= page * rowsPerPage;
        const shouldDecrementPage = notOnFirstPage && notEnoughRowsToShow;
        if (shouldDecrementPage) {
            setPage((prev) => prev - 1);
        }
    };
    const isValueAFunction = (value) => typeof value === 'function';
    const isValueANode = (value) => typeof value === 'object';
    const formatByType = (value, type) => {
        switch (type) {
            case ColumnType.PERCENTAGE:
                return `${parseFloat(value).toLocaleString('en-US', {
                    minimumFractionDigits: 2,
                })}%`;
            case ColumnType.CURRENCY:
                return `$${parseFloat(value).toLocaleString('en-US', {
                    minimumFractionDigits: 2,
                })}`;
            case ColumnType.NUMBER:
                return parseFloat(value).toLocaleString('en-US');
            default:
                if (isValueAFunction(value)) {
                    return value();
                }
                if (isValueANode(value)) {
                    return value;
                }
                return `${value}`;
        }
    };
    const renderValue = (value, column) => {
        if (typeof value === 'function')
            return value();
        if (!value && !NOT_CONSIDERED_EMPTY.includes(value)) {
            if (column.renderEmpty)
                return column.renderEmpty();
            return '';
        }
        return formatByType(value, column.type || ColumnType.OTHER);
    };
    const sortData = (a, b) => {
        var _a;
        if (!sortColumn || !sortDirection)
            return 0;
        /* Custom sort function is passed */
        const custom = (_a = columns.find((c) => c.id === sortColumn)) === null || _a === void 0 ? void 0 : _a.customSorter;
        if (custom) {
            return custom(a[sortColumn], b[sortColumn], sortDirection);
        }
        /* One of the values is empty */
        if (!a[sortColumn] && !NOT_CONSIDERED_EMPTY.includes(a[sortColumn])) {
            return -1 * sortDirection;
        }
        if (!b[sortColumn] && !NOT_CONSIDERED_EMPTY.includes(b[sortColumn])) {
            return sortDirection;
        }
        /* Both values are numbers, can compare mathematically */
        if (typeof a[sortColumn] === 'number' && typeof b[sortColumn] === 'number') {
            const lower = a[sortColumn] < b[sortColumn];
            return lower ? -1 * sortDirection : sortDirection;
        }
        /* All other cases */
        const lower = `${a[sortColumn]}`.toLocaleLowerCase() < `${b[sortColumn]}`.toLocaleLowerCase();
        return lower ? -1 * sortDirection : sortDirection;
    };
    const getColumnTotal = (column) => {
        return rows
            .reduce((acc, curr) => {
            const val = parseFloat(`${curr[column]}`);
            if (Number.isNaN(val))
                return acc;
            return acc.add(val);
        }, Big(0))
            .toNumber();
    };
    const getSelectedColumns = () => {
        if (!columnSelector || !ColumnSelectorProps)
            return columns;
        const selectedColumns = [];
        columnsSelected.forEach((columnId) => {
            columns.forEach((column) => {
                if (column.id === columnId) {
                    selectedColumns.push(column);
                }
            });
        });
        return selectedColumns;
    };
    const getRowPlaceholderColSpan = () => {
        const columnSelectorCount = columnSelector ? 1 : 0;
        return getSelectedColumns().length + columnSelectorCount;
    };
    const renderRows = () => {
        return rows
            .sort(sortData)
            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
            .map((row, index) => (React.createElement(TableRow, { id: row.id, hover: rowHover, key: row.id, className: isEven(index) ? classes.evenRow : classes.oddRow }, row.placeholder ? (React.createElement("td", { className: isEven(index) ? classes.evenRow : classes.oddRow, colSpan: getRowPlaceholderColSpan() }, row.placeholder)) : (React.createElement(React.Fragment, null,
            getSelectedColumns().map((column) => {
                const value = row[column.id];
                return (React.createElement(TableCell, { id: ix(CELL_ID, row.id, column.id), className: classes.tableBodyCell, style: column.styles, key: ix(CELL_ID, row.id, column.id) }, renderValue(value, column)));
            }),
            columnSelector && ColumnSelectorProps && (React.createElement(TableCell, { id: ix(CELL_ID, row.id, COLUMN_SELECTOR_ID), className: classes.tableBodyCell, key: ix(CELL_ID, row.id, COLUMN_SELECTOR_ID) })))))));
    };
    const renderTotalsRow = () => {
        if (!totalsRow)
            return null;
        return (React.createElement(TableRow, { id: TOTALS_ID, key: TOTALS_ID, className: classes.totalsRow },
            getSelectedColumns().map((column) => {
                const shouldDisplayLabel = column.id === totalsRow.labelColumn;
                const shouldDisplayTotal = !shouldDisplayLabel && column.type && TOTALABLE_COLUMN_TYPES.includes(column.type);
                const shouldDisplayEmpty = !shouldDisplayLabel && !shouldDisplayTotal;
                return (React.createElement(TableCell, { id: ix(TOTALS_ID, column.id), className: classes.tableBodyCell, key: column.id },
                    shouldDisplayLabel && React.createElement("span", { className: classes.totalsRowLabel }, totalsRow.label || TOTALS_ID),
                    shouldDisplayTotal && (React.createElement("span", { className: classes.totalsRowLabel }, renderValue(getColumnTotal(column.id), column))),
                    shouldDisplayEmpty &&
                        ((totalsRow === null || totalsRow === void 0 ? void 0 : totalsRow.renderEmpty) ? (totalsRow === null || totalsRow === void 0 ? void 0 : totalsRow.renderEmpty(column.id)) : (React.createElement("span", { className: classes.totalsRowEmpty }, "N/A")))));
            }),
            columnSelector && ColumnSelectorProps && (React.createElement(TableCell, { id: ix(TOTALS_ID, COLUMN_SELECTOR_ID), key: ix(TOTALS_ID, COLUMN_SELECTOR_ID), className: classes.tableBodyCell }))));
    };
    const sortByColumn = (columnId) => () => {
        setSortColumn(columnId);
        setSortDirection(sortDirection === 1 ? -1 : 1);
    };
    const openColumnSelector = () => {
        setColumnSelectorOpen(true);
    };
    const closeColumnSelector = () => {
        setColumnSelectorOpen(false);
    };
    const getColumnSelectorColumns = () => {
        const selectedColumns = [];
        const unselectedColumns = [];
        for (let i = 0; i < columns.length; i++) {
            const column = columns[i];
            if (columnsSelected.includes(column.id)) {
                selectedColumns.push({
                    label: column.label,
                    value: column.id,
                    alwaysEnabled: columnSelectorAlwaysEnabled.has(column.label),
                    selected: columnsSelected.includes(column.id),
                    pinned: columnSelectorPinnedColumns.includes(column.label),
                    type: columnSelectorDefaultMetrics.has(column.label) ? 'metric' : 'event',
                });
            }
            else {
                unselectedColumns.push({
                    label: column.label,
                    value: column.id,
                    alwaysEnabled: columnSelectorAlwaysEnabled.has(column.label),
                    selected: columnsSelected.includes(column.id),
                    pinned: columnSelectorPinnedColumns.includes(column.label),
                    type: columnSelectorDefaultMetrics.has(column.label) ? 'metric' : 'event',
                });
            }
        }
        const sortedSelectedColumns = [];
        columnsSelected.forEach((columnId) => {
            selectedColumns.forEach((selected) => {
                if (columnId === selected.value) {
                    sortedSelectedColumns.push(selected);
                }
            });
        });
        const columnSelectorColumns = new Map();
        for (let i = 0; i < sortedSelectedColumns.length; i++) {
            columnSelectorColumns.set(sortedSelectedColumns[i].value, sortedSelectedColumns[i]);
        }
        for (let i = 0; i < unselectedColumns.length; i++) {
            columnSelectorColumns.set(unselectedColumns[i].value, unselectedColumns[i]);
        }
        return columnSelectorColumns;
    };
    const handleColumnSelectorSave = (appliedColumns, closeAfter) => {
        const selectedIds = [];
        appliedColumns.map((column) => {
            selectedIds.push(column.value);
        });
        setColumnsSelected(selectedIds);
        if (closeAfter) {
            closeColumnSelector();
        }
    };
    useEffect(decrementPageIfNeeded, [page, rows.length, rowsPerPage]);
    return (React.createElement(React.Fragment, null,
        React.createElement(Table, { className: cx(className, classes.table), id: id },
            React.createElement(TableHead, null,
                React.createElement(TableRow, null,
                    getSelectedColumns().map((column) => (React.createElement(TableCell, { id: column.id, className: cx(classes.columnHeader, {
                            clickable: column.sortable,
                        }), key: column.id, align: column.align || 'left', size: "small", onClick: column.sortable ? sortByColumn(column.id) : () => { } },
                        React.createElement("div", { className: classes.tooltipLabelContainer },
                            React.createElement("div", null, column.label.toUpperCase()),
                            column.sortable && (React.createElement("div", { className: classes.sortIcons },
                                React.createElement("div", { className: cx(classes.sortIcon, 'up', {
                                        active: sortColumn === column.id && sortDirection === 1,
                                    }) }),
                                React.createElement("div", { className: cx(classes.sortIcon, 'down', {
                                        active: sortColumn === column.id && sortDirection === -1,
                                    }) }))),
                            column.tooltip)))),
                    columnSelector && ColumnSelectorProps && (React.createElement(TableCell, { id: ix(id, HEADER_COLUMN_SELECTOR), key: ix(id, HEADER_COLUMN_SELECTOR), className: cx(classes.columnHeader, classes.columnSelectorHeader), align: "center", size: "small", onClick: openColumnSelector },
                        React.createElement("div", { className: cx(classes.tooltipLabelContainer, classes.columnSelectorLabelContainer) },
                            React.createElement(Icon, null, "window")))))),
            rows.length ? (React.createElement(TableBody, null,
                totalsRow && renderTotalsRow(),
                renderRows())) : (React.createElement(TableBody, null,
                React.createElement(TableRow, null,
                    React.createElement(TableCell, { colSpan: columns.length, className: classes.noDataMessage }, noDataMessage || React.createElement(Typography, null, "No data")))))),
        !removeFooter && (React.createElement(TablePagination, { rowsPerPageOptions: rowsPerPageOptions, count: rows.length, rowsPerPage: rowsPerPage, page: page, onPageChange: onPageChange, onRowsPerPageChange: onRowsPerPageChange })),
        columnSelector && ColumnSelectorProps && (React.createElement(ColumnSelector, { id: ix(id, COLUMN_SELECTOR_ID), open: columnSelectorOpen, onClose: closeColumnSelector, onSave: (appliedColumns, closeAfter) => handleColumnSelectorSave(appliedColumns, closeAfter), columns: getColumnSelectorColumns(), eventsPopularity: ColumnSelectorProps.eventsPopularity, minimumSelectedColumns: ColumnSelectorProps.minimumSelectedColumns, maximumSelectedColumns: ColumnSelectorProps.maximumSelectedColumns, onSelectedColumns: ColumnSelectorProps.onSelectedColumns }))));
}
//# sourceMappingURL=PaginationTable.js.map